// Small, dependency-free platform probes used to tailor the install tutorial
// and push opt-in hints. User-agent sniffing is deliberately narrow — we only
// need to distinguish the install gestures, not identify the exact device.

import { useEffect, useState } from 'react';

export type InstallPlatform = 'ios' | 'android' | 'desktop';

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports as "MacIntel" with touch — catch it via maxTouchPoints.
  const iPadOsDesktop =
    navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
  return /iP(ad|hone|od)/.test(navigator.userAgent) || iPadOsDesktop;
}

export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
}

/** True when running as an installed PWA (home-screen / standalone window). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

export function getInstallPlatform(): InstallPlatform {
  if (isIos()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
}

// --- beforeinstallprompt capture -------------------------------------------
// Chrome/Edge fire `beforeinstallprompt` once, early, and only if the PWA is
// installable. We stash the deferred event so the install screen can offer a
// one-tap install button whenever the user gets there. Captured at module load
// so we never miss the (single) event.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(available: boolean) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    listeners.forEach((fn) => fn(true));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn(false));
  });
}

/**
 * Exposes whether a native install prompt is currently available and a
 * `promptInstall()` that triggers it. Returns `{ available: false }` on iOS /
 * already-installed / unsupported browsers, where the manual steps apply.
 */
export function useInstallPrompt(): {
  available: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
} {
  const [available, setAvailable] = useState(deferredPrompt !== null);

  useEffect(() => {
    const fn = (next: boolean) => setAvailable(next);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return 'unavailable' as const;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    listeners.forEach((fn) => fn(false));
    return outcome;
  };

  return { available, promptInstall };
}
